import { NodeThumb } from '@/components/value-chain/explorer/NodeThumb';
import { evidenceLabel, shortName } from '@/components/value-chain/nodeVisuals';
import type { TreeIndex } from '@/components/value-chain/treeIndex';
import { ancestorsOf, childrenOf, companiesOf, subtreeCompanyCounts } from '@/components/value-chain/treeIndex';
import type { ValueChainGraph, ValueChainNode } from '@/types/valueChain';

type Props = {
  graph: ValueChainGraph;
  index: TreeIndex;
  selected: ValueChainNode;
  onSelect: (id: string) => void;
};

export function ChainView({ graph, index, selected, onSelect }: Props) {
  const path = ancestorsOf(index, selected.id);
  const trail = path.length ? path : [selected];

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-6">
      <h2 className="mb-1 text-sm font-semibold text-gray-900">Chain from {graph.name}</h2>
      <p className="mb-5 text-[13px] text-gray-500">
        One path at a time — click any step to inspect it. Double-click children in Hierarchy to drill.
      </p>
      <div className="flex flex-col gap-3">
        {trail.map((node, i) => {
          const kids = childrenOf(index, node.id);
          return (
            <div key={node.id}>
              <button
                type="button"
                onClick={() => onSelect(node.id)}
                className={`flex w-full items-center gap-3 rounded-2xl border px-3 py-3 text-left ${
                  node.id === selected.id
                    ? 'border-blue-300 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <span className="w-6 text-center text-[11px] text-gray-400">{i + 1}</span>
                <NodeThumb node={node} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">
                    {shortName(node.name)}
                  </span>
                  <span className="block text-[12px] text-gray-400">{node.type}</span>
                </span>
              </button>
              {node.id === selected.id && kids.length > 0 ? (
                <div className="mt-2 ml-12 grid gap-2 md:grid-cols-2">
                  {kids.map((child) => (
                    <button
                      key={child.id}
                      type="button"
                      onClick={() => onSelect(child.id)}
                      className="flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-3 py-2 text-left hover:border-gray-300"
                    >
                      <NodeThumb node={child} size="sm" />
                      <span className="truncate text-[13px] text-gray-800">{shortName(child.name)}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function OpportunitiesView({ index, selected, onSelect }: Props) {
  const rows = collectOpportunities(index, selected.id);
  return (
    <CatalogLayout
      title="Opportunities"
      subtitle="Unknown or inferred suppliers, and high-rank parts still missing a confirmed manufacturer."
      rows={rows}
      onSelect={onSelect}
    />
  );
}

export function ExposureView({ index, selected }: Omit<Props, 'onSelect' | 'graph'>) {
  const rows = subtreeCompanyCounts(index, selected.id);
  const max = rows[0]?.count ?? 1;
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-6">
      <h2 className="text-sm font-semibold text-gray-900">Company Exposure</h2>
      <p className="mt-1 mb-5 text-[13px] text-gray-500">
        Relative mention count under {shortName(selected.name)}. Not revenue.
      </p>
      <div className="space-y-2">
        {rows.slice(0, 40).map((row) => (
          <div key={row.company.id} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5">
            <NodeThumb node={row.company} size="sm" />
            <span className="w-56 truncate text-sm text-gray-900">{shortName(row.company.name)}</span>
            <span className="h-2.5 flex-1 rounded-full bg-gray-100">
              <span
                className="block h-2.5 rounded-full bg-blue-400"
                style={{ width: `${Math.max(6, (row.count / max) * 100)}%` }}
              />
            </span>
            <span className="w-10 text-right text-[12px] text-gray-500">{row.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ManufacturingView({ index, selected, onSelect }: Omit<Props, 'graph'>) {
  const rows = subtreeCompanyCounts(index, selected.id).flatMap((row) =>
    (index.suppliedNodes.get(row.company.id) ?? []).slice(0, 8).map((partId) => {
      const part = index.byId.get(partId);
      if (!part) return null;
      return {
        id: `${row.company.id}:${part.id}`,
        node: part,
        meta: `${shortName(row.company.name)} · ${evidenceLabel(part.evidence_status)}`,
      };
    }),
  ).filter((row): row is { id: string; node: ValueChainNode; meta: string } => Boolean(row));

  return (
    <CatalogLayout
      title="Manufacturing"
      subtitle={`Parts under ${shortName(selected.name)} with a mapped manufacturer.`}
      rows={rows.slice(0, 80)}
      onSelect={onSelect}
    />
  );
}

export function BomView({ index, selected, onSelect }: Omit<Props, 'graph'>) {
  const rows = childrenOf(index, selected.id).map((node) => {
    const edge = index.inboundBom.get(node.id);
    const supplier = companiesOf(index, node.id)[0];
    return {
      id: node.id,
      node,
      meta: [
        edge?.rank != null ? `Rank ${edge.rank}` : null,
        supplier ? shortName(supplier.name) : node.manufacturer || 'Unknown supplier',
        evidenceLabel(node.evidence_status),
      ]
        .filter(Boolean)
        .join(' · '),
    };
  });
  return (
    <CatalogLayout
      title="BOM Estimate"
      subtitle={`Direct children of ${shortName(selected.name)}, ranked by hop importance/cost.`}
      rows={rows}
      onSelect={onSelect}
    />
  );
}

export function DirectoryView({
  title,
  subtitle,
  nodes,
  onSelect,
}: {
  title: string;
  subtitle: string;
  nodes: ValueChainNode[];
  onSelect: (id: string) => void;
}) {
  return (
    <CatalogLayout
      title={title}
      subtitle={subtitle}
      rows={nodes.slice(0, 120).map((node) => ({
        id: node.id,
        node,
        meta: node.type,
      }))}
      onSelect={onSelect}
    />
  );
}

function CatalogLayout({
  title,
  subtitle,
  rows,
  onSelect,
}: {
  title: string;
  subtitle: string;
  rows: Array<{ id: string; node: ValueChainNode; meta: string }>;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-6">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      <p className="mt-1 mb-5 text-[13px] text-gray-500">{subtitle}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing mapped for this view yet.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <button
              key={row.id}
              type="button"
              onClick={() => onSelect(row.node.id)}
              className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left hover:border-gray-400"
            >
              <NodeThumb node={row.node} size="sm" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-gray-900">
                  {shortName(row.node.name)}
                </span>
                <span className="block truncate text-[12px] text-gray-400">{row.meta}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function collectOpportunities(
  index: TreeIndex,
  nodeId: string,
): Array<{ id: string; node: ValueChainNode; meta: string }> {
  const rows: Array<{ id: string; node: ValueChainNode; meta: string }> = [];
  const stack = [nodeId];
  const seen = new Set<string>();
  while (stack.length && rows.length < 80) {
    const id = stack.pop();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const node = index.byId.get(id);
    if (node && node.type !== 'Company') {
      const companies = companiesOf(index, id);
      const unknown =
        companies.length === 0 ||
        companies.some((company) => /unknown/i.test(company.name)) ||
        /unknown/i.test(node.manufacturer ?? '');
      const inferred = node.evidence_status === 'inferred' || node.evidence_status === 'unknown';
      const rank = index.inboundBom.get(id)?.rank;
      if (unknown || inferred) {
        rows.push({
          id,
          node,
          meta: [
            rank != null ? `Rank ${rank}` : null,
            unknown ? 'Supplier gap' : null,
            inferred ? evidenceLabel(node.evidence_status) : null,
          ]
            .filter(Boolean)
            .join(' · '),
        });
      }
    }
    stack.push(...(index.children.get(id) ?? []));
  }
  return rows;
}
